// Phase 7 D3 — Track C T-C4. /settings/devices page.
//
// Lists every active session + refresh-token row. The "Revoke all but
// current" control sits at the top of the list; per-row Revoke (Q-11)
// surfaces in DeviceRow.

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import { useDevices, useRevokeDevice, useRevokeAllButCurrent } from '../../../hooks/useDevices.js';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/Card.js';
import { Button } from '../../../components/ui/Button.js';
import { SkeletonRow } from '../../../components/ui/Skeleton.js';
import { EmptyState } from '../../../components/ui/EmptyState.js';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '../../../components/ui/Dialog.js';
import {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastClose,
} from '../../../components/ui/Toast.js';
import { DeviceRow } from './DeviceRow.js';

export function Devices(): JSX.Element {
  React.useEffect(() => {
    document.title = `DailyDeck — ${copy.settings.shell.nav.devices}`;
  }, []);

  const query = useDevices();
  const revoke = useRevokeDevice();
  const revokeAll = useRevokeAllButCurrent();

  const [revokeAllOpen, setRevokeAllOpen] = React.useState(false);
  const [toast, setToast] = React.useState<{ title: string } | null>(null);

  function handleRevoke(id: string): void {
    revoke.mutate(
      { id },
      {
        onSuccess: () => setToast({ title: copy.settings.devices.revokeSuccessToastTitle }),
      },
    );
  }

  function handleRevokeAll(): void {
    revokeAll.mutate(undefined, {
      onSuccess: () => {
        setRevokeAllOpen(false);
        setToast({ title: copy.settings.devices.revokeAllSuccessToastTitle });
      },
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2
            id="settings-section-heading"
            className="text-title-2 font-semibold text-text-primary"
          >
            {copy.settings.devices.title}
          </h2>
          <p className="text-body text-text-secondary mt-1">
            {copy.settings.devices.description}
          </p>
        </div>
        <Dialog open={revokeAllOpen} onOpenChange={setRevokeAllOpen}>
          <DialogTrigger asChild>
            <Button variant="secondary" type="button">
              {copy.settings.devices.revokeAllButton}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{copy.settings.devices.revokeAllDialogTitle}</DialogTitle>
              <DialogDescription>
                {copy.settings.devices.revokeAllDialogBody}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary" type="button">
                  {copy.settings.devices.revokeDialogCancel}
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                type="button"
                disabled={revokeAll.isPending}
                aria-busy={revokeAll.isPending}
                onClick={handleRevokeAll}
              >
                {copy.settings.devices.revokeAllDialogConfirm}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{copy.settings.devices.title}</CardTitle>
          <CardDescription>{copy.settings.devices.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {query.isPending && (
            <div className="rounded-lg border border-border-subtle bg-surface-raised">
              <SkeletonRow />
              <SkeletonRow />
            </div>
          )}

          {query.isError && (
            <EmptyState
              title={copy.settings.devices.loadErrorTitle}
              description={copy.settings.devices.loadErrorBody}
            />
          )}

          {query.isSuccess && query.data.devices.length === 0 && (
            <EmptyState
              title={copy.settings.devices.emptyTitle}
              description={copy.settings.devices.emptyBody}
            />
          )}

          {query.isSuccess && query.data.devices.length > 0 && (
            <ul role="list" data-testid="devices-list" className="flex flex-col">
              {query.data.devices.map((device) => (
                <DeviceRow
                  key={device.id}
                  device={device}
                  onConfirmRevoke={handleRevoke}
                  isRevoking={revoke.isPending}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {toast !== null && (
        <ToastProvider duration={4000}>
          <Toast open onOpenChange={(open) => !open && setToast(null)}>
            <ToastTitle>{toast.title}</ToastTitle>
            <ToastClose />
          </Toast>
          <ToastViewport />
        </ToastProvider>
      )}
    </div>
  );
}
